import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Building2, Mail, Lock, Eye, EyeOff, Shield, AlertTriangle, 
  Check, X, Loader2, ArrowLeft, KeyRound, Smartphone, Sun, Moon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Password strength colors
const getStrengthColor = (strength) => {
  if (strength < 30) return 'bg-red-500';
  if (strength < 50) return 'bg-orange-500';
  if (strength < 70) return 'bg-yellow-500';
  if (strength < 90) return 'bg-lime-500';
  return 'bg-green-500';
};

const getStrengthLabel = (strength) => {
  if (strength < 30) return 'Très faible';
  if (strength < 50) return 'Faible';
  if (strength < 70) return 'Moyen';
  if (strength < 90) return 'Fort';
  return 'Très fort';
};

const LoginSecure = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('login'); // login, forgot, reset, 2fa
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  
  // Password reset
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Password validation
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  // Error handling
  const [errorMessage, setErrorMessage] = useState('');
  const [lockoutMinutes, setLockoutMinutes] = useState(0);
  
  const { login, isAuthenticated } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const totpInputRef = useRef(null);

  // Check for reset token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setResetToken(token);
      setMode('reset');
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Focus TOTP input when 2FA mode is activated
  useEffect(() => {
    if (mode === '2fa' && totpInputRef.current) {
      totpInputRef.current.focus();
    }
  }, [mode]);

  // Validate password in real-time for reset mode
  useEffect(() => {
    if (newPassword && mode === 'reset') {
      validatePassword(newPassword);
    }
  }, [newPassword, mode]);

  const validatePassword = async (pwd) => {
    try {
      const response = await axios.post(`${API_URL}/auth/password-strength`, {
        password: pwd
      });
      setPasswordStrength(response.data.strength);
      setPasswordValidation(response.data.validation);
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(`${API_URL}/auth/login/secure`, {
        email,
        password,
        totp_code: totpCode || null
      });

      if (response.data.require_2fa) {
        setPendingUser(response.data.user);
        setMode('2fa');
        setLoading(false);
        return;
      }

      // Store tokens
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      
      toast.success('Connexion réussie !');
      
      // Use the auth context login
      await login(email, password);
      navigate('/dashboard');

    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;

      if (status === 423) {
        // Account locked
        const minutes = parseInt(detail?.match(/(\d+)/)?.[1] || '15');
        setLockoutMinutes(minutes);
        setErrorMessage(`Compte verrouillé pour ${minutes} minutes`);
        toast.error(`Trop de tentatives. Réessayez dans ${minutes} minutes.`);
      } else if (status === 401) {
        setErrorMessage('Email ou mot de passe incorrect');
        toast.error('Identifiants incorrects');
      } else {
        setErrorMessage(detail || 'Erreur de connexion');
        toast.error('Erreur de connexion');
      }
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      toast.success('Email envoyé ! Vérifiez votre boîte de réception.');
      setMode('login');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }

    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (!passwordValidation?.is_valid) {
      setErrorMessage('Le mot de passe ne respecte pas les critères de sécurité');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      toast.success('Mot de passe réinitialisé ! Vous pouvez vous connecter.');
      setMode('login');
      setResetToken('');
      navigate('/login');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) {
        setErrorMessage(detail.errors.join(', '));
      } else {
        setErrorMessage(detail || 'Token invalide ou expiré');
      }
      toast.error('Erreur lors de la réinitialisation');
    }

    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login/secure`, {
        email,
        password,
        totp_code: totpCode
      });

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      
      toast.success('Connexion réussie !');
      await login(email, password);
      navigate('/dashboard');

    } catch (error) {
      setErrorMessage('Code 2FA invalide');
      toast.error('Code invalide');
      setTotpCode('');
    }

    setLoading(false);
  };

  // Render password validation checklist
  const renderPasswordChecklist = () => {
    if (!passwordValidation) return null;

    const items = [
      { key: 'min_length', label: '12 caractères minimum', value: passwordValidation.min_length },
      { key: 'has_uppercase', label: 'Une majuscule', value: passwordValidation.has_uppercase },
      { key: 'has_lowercase', label: 'Une minuscule', value: passwordValidation.has_lowercase },
      { key: 'has_digit', label: 'Un chiffre', value: passwordValidation.has_digit },
      { key: 'has_special', label: 'Un caractère spécial', value: passwordValidation.has_special },
      { key: 'no_sequences', label: 'Pas de séquences (123, abc)', value: passwordValidation.no_sequences },
    ];

    return (
      <div className="mt-3 space-y-1">
        <div className="flex items-center gap-2 mb-2">
          <Progress value={passwordStrength} className={`h-2 flex-1`} />
          <span className={`text-xs font-medium ${
            passwordStrength < 50 ? 'text-red-500' : passwordStrength < 70 ? 'text-yellow-500' : 'text-green-500'
          }`}>
            {getStrengthLabel(passwordStrength)}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-1">
          {items.map(item => (
            <div 
              key={item.key}
              className={`flex items-center gap-1 text-xs ${item.value ? 'text-green-600' : 'text-gray-400'}`}
            >
              {item.value ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              {item.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`flex min-h-screen items-center justify-center transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-[hsl(220,16%,11%)]' 
          : 'bg-slate-50'
      }`}
      style={!isDarkMode ? {
        backgroundImage: `url('https://images.unsplash.com/photo-1765046255500-ce203b84c307?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : undefined}
      data-testid="login-page-secure"
    >
      {/* Overlay gradient - only in light mode */}
      {!isDarkMode && (
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 to-slate-900/90"></div>
      )}
      
      {/* Dark mode toggle - fixed position */}
      <div 
        className={`fixed top-6 right-6 z-50 flex items-center gap-2 px-3 py-2 rounded-full transition-colors ${
          isDarkMode 
            ? 'bg-[hsl(220,14%,14%)] border border-[hsl(220,13%,20%)]' 
            : 'bg-white/20 backdrop-blur-sm border border-white/30'
        }`}
        data-testid="login-dark-mode-toggle"
      >
        <Sun className={`h-4 w-4 transition-colors ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-amber-300'}`} />
        <Switch
          checked={isDarkMode}
          onCheckedChange={toggleDarkMode}
          className="data-[state=checked]:bg-indigo-600"
        />
        <Moon className={`h-4 w-4 transition-colors ${isDarkMode ? 'text-indigo-400' : 'text-white/60'}`} />
      </div>
      
      <Card 
        className={`relative z-10 w-full max-w-md shadow-2xl transition-colors ${
          isDarkMode 
            ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,13%,20%)] text-[hsl(220,13%,75%)]' 
            : 'bg-white border-slate-200'
        }`} 
        data-testid="login-card-secure"
      >
        <CardHeader className="text-center pb-2">
          <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg ${
            isDarkMode ? 'bg-indigo-600' : 'bg-indigo-700'
          }`}>
            {mode === '2fa' ? (
              <Smartphone className="h-8 w-8 text-[hsl(220,13%,95%)]" />
            ) : mode === 'reset' ? (
              <KeyRound className="h-8 w-8 text-[hsl(220,13%,95%)]" />
            ) : (
              <Building2 className="h-8 w-8 text-[hsl(220,13%,95%)]" />
            )}
          </div>
          <CardTitle className={`text-2xl font-bold ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : ''}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
            {mode === 'login' && 'B2RCS'}
            {mode === 'forgot' && 'Mot de passe oublié'}
            {mode === 'reset' && 'Nouveau mot de passe'}
            {mode === '2fa' && 'Vérification 2FA'}
          </CardTitle>
          <CardDescription className={isDarkMode ? 'text-[hsl(215,16%,60%)]' : ''}>
            {mode === 'login' && 'Connectez-vous à votre compte'}
            {mode === 'forgot' && 'Entrez votre email pour recevoir un lien'}
            {mode === 'reset' && 'Créez un nouveau mot de passe sécurisé'}
            {mode === '2fa' && 'Entrez le code de votre application'}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          {/* Error message */}
          {errorMessage && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
              isDarkMode 
                ? 'bg-red-950/50 border border-red-900/50 text-red-400' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {errorMessage}
            </div>
          )}

          {/* Lockout warning */}
          {lockoutMinutes > 0 && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
              isDarkMode 
                ? 'bg-amber-950/50 border border-amber-900/50 text-amber-400' 
                : 'bg-amber-50 border border-amber-200 text-amber-700'
            }`}>
              <Shield className="h-4 w-4 flex-shrink-0" />
              Compte temporairement verrouillé pour {lockoutMinutes} minutes
            </div>
          )}

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className={isDarkMode ? 'text-[hsl(220,13%,70%)]' : ''}>Email</Label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-3 h-5 w-5 ${isDarkMode ? 'text-[hsl(220,14%,50%)]' : 'text-slate-400'}`} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`pl-10 h-12 rounded-xl ${
                      isDarkMode 
                        ? 'bg-[hsl(220,13%,16%)] border-[hsl(220,14%,25%)] text-white placeholder:text-[hsl(220,14%,45%)]' 
                        : ''
                    }`}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className={isDarkMode ? 'text-[hsl(220,13%,70%)]' : ''}>Mot de passe</Label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-3 h-5 w-5 ${isDarkMode ? 'text-[hsl(220,14%,50%)]' : 'text-slate-400'}`} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`pl-10 pr-10 h-12 rounded-xl ${
                      isDarkMode 
                        ? 'bg-[hsl(220,13%,16%)] border-[hsl(220,14%,25%)] text-white placeholder:text-[hsl(220,14%,45%)]' 
                        : ''
                    }`}
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-3 ${isDarkMode ? 'text-[hsl(220,14%,50%)] hover:text-[hsl(220,14%,70%)]' : 'text-slate-400 hover:text-slate-600'}`}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className={`text-sm hover:underline ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading || lockoutMinutes > 0}
                className="w-full h-12 rounded-full bg-indigo-700 font-semibold shadow-lg hover:shadow-xl hover:bg-indigo-800 transition-all"
                data-testid="login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>
          )}

          {/* FORGOT PASSWORD FORM */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-indigo-700 font-semibold"
                data-testid="send-reset-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  'Envoyer le lien'
                )}
              </Button>

              <button
                type="button"
                onClick={() => setMode('login')}
                className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-800"
              >
                <ArrowLeft className="h-4 w-4" />
                Retour à la connexion
              </button>
            </form>
          )}

          {/* RESET PASSWORD FORM */}
          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Nouveau mot de passe"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 rounded-xl"
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {renderPasswordChecklist()}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirmer le mot de passe"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <X className="h-3 w-3" /> Les mots de passe ne correspondent pas
                  </p>
                )}
                {confirmPassword && newPassword === confirmPassword && (
                  <p className="text-xs text-green-500 flex items-center gap-1">
                    <Check className="h-3 w-3" /> Les mots de passe correspondent
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !passwordValidation?.is_valid || newPassword !== confirmPassword}
                className="w-full h-12 rounded-full bg-indigo-700 font-semibold"
                data-testid="reset-password-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Réinitialisation...
                  </>
                ) : (
                  'Réinitialiser le mot de passe'
                )}
              </Button>
            </form>
          )}

          {/* 2FA FORM */}
          {mode === '2fa' && (
            <form onSubmit={handle2FASubmit} className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-sm text-slate-600">
                  Entrez le code à 6 chiffres de votre application d'authentification
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="totp-code">Code de vérification</Label>
                <Input
                  ref={totpInputRef}
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="h-14 text-center text-2xl tracking-widest font-mono rounded-xl"
                  required
                  data-testid="totp-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                className="w-full h-12 rounded-full bg-indigo-700 font-semibold"
                data-testid="verify-2fa-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  'Vérifier'
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setTotpCode('');
                  setPendingUser(null);
                }}
                className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-800"
              >
                <ArrowLeft className="h-4 w-4" />
                Utiliser un autre compte
              </button>
            </form>
          )}
        </CardContent>

        <CardFooter className="flex flex-col items-center pt-0">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Shield className="h-3 w-3" />
            Connexion sécurisée avec chiffrement SSL
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default LoginSecure;
